import {
  AudioDecoderDowngradeState,
  BannedReason,
  ConnectionState,
  CustomMessage,
  DeviceInfo,
  NetworkQuality,
  PlayerState,
  VideoDecoderDowngradeState,
  PEER_LEAVE_REASON
} from 'trtc-js-sdk-core';
import { FullScreenStateChangedEvent, PictureInPictureStateChangedEvent, TRTCDeviceAction, TRTCDeviceType, TRTCStatistics, TRTCStreamType, RealtimeTranscriberMessage } from './type';
import RtcError from './common/rtc-error';

/**
 * @typedef TRTCStatistics TRTC statistics
 * @property {number} rtt The round-trip time from SDK to TRTC server(SDK -> TRTC server -> SDK). Unit: ms.
 * @property {number} upLoss Uplink loss rate from SDK to TRTC server. Unit: %
 * @property {number} downLoss Downlink loss rate from TRTC server to SDK. Unit: %
 * @property {number} bytesSent Total bytes sent, including signaling data and media data. Unit: bytes.
 * @property {number} bytesReceived Total bytes received, including signaling data and media data. Unit: bytes.
 * @property {TRTCLocalStatistics} localStatistics Local statistics.
 * @property {TRTCRemoteStatistics[]} remoteStatistics Remote statistics.
 */

/**
 * Local statistics
 * @typedef TRTCLocalStatistics
 * @property {TRTCAudioStatistic} audio Local audio statistics
 * @property {TRTCVideoStatistic[]} video Local video statistics
 */

/**
 * Remote statistics.
 * @typedef TRTCRemoteStatistics
 * @property {string} userId The userId of remote user
 * @property {TRTCAudioStatistic} audio Remote audio statistics
 * @property {TRTCVideoStatistic[]} video Remote video statistics
 */

/**
 * Audio statistics
 * @typedef TRTCAudioStatistic
 * @property {number} bitrate Audio bitrate. Unit: kbps
 * @property {number} audioLevel Audio level. Value: float from 0 to 1.
 * @property {number} jitterBufferDelay Playback delay, unit: ms. Since `v5.11.0`
 * @property {number} point2pointDelay End-to-end delay, unit: ms. This indicator is an estimate and may be affected by network quality. Since `v5.11.0`
 */

/** Video statistics
 * @typedef TRTCVideoStatistic
 * @property {number} bitrate Video bitrate. Unit: kbps
 * @property {number} width Video width
 * @property {number} height Video height
 * @property {number} frameRate Video frameRate
 * @property {'big'|'small'|'sub'} videoType Video type: big, small, sub.
 * @property {number} jitterBufferDelay Playback delay, unit: ms. Since `v5.11.0`
 * @property {number} point2pointDelay End-to-end delay, unit: ms. This indicator is an estimate and may be affected by network quality. Since `v5.11.0`
 */

/** enableAudioFrameEvent Options
 * @typedef EnableAudioFrameEventOptions
 * @property {boolean} enable Whether to enable callback of audio frame
 * @property {string} userId The userId of the monitored user. '' indicates local microphone data, '*' indicates monitoring the audio data of all remote users.
 * @property {number=} [sampleRate=48000] Set the sampling rate of pcm data, the default is 48000. Support 8000, 16000, 32000, 44100, 48000
 * @property {number=} [channelCount=1] Set the number of channels of pcm data, the default is 1. Support 1, 2
 * @property {MessagePort=} port Set the MessagePort for the pcm callback
 */

/**
 * @typedef ResumeRemotePlayerOptions
 * @property {string} userId remote userId.
 * @property {string=} streamType {@link module:TYPE.STREAM_TYPE_MAIN TRTC.TYPE.STREAM_TYPE_MAIN} {@link module:TYPE.STREAM_TYPE_SUB TRTC.TYPE.STREAM_TYPE_SUB}
 */

/**
 * @typedef PauseRemotePlayerOptions
 * @property {string} userId remote userId.
 * @property {string=} streamType {@link module:TYPE.STREAM_TYPE_MAIN TRTC.TYPE.STREAM_TYPE_MAIN} {@link module:TYPE.STREAM_TYPE_SUB TRTC.TYPE.STREAM_TYPE_SUB}
 */

/**
 * **TRTC Event List**<br>
 * <br>
 * Listen to specific events through {@link TRTC#on trtc.on(TRTC.EVENT.XXX)}. You can use these events to manage the room user list, manage user stream state, and perceive network state. The following is a detailed introduction to the events.
 * > !
 * > - Events need to be listened to before they are triggered, so that you can receive the corresponding event notifications. Therefore, it is recommended to complete event listening before entering the room, so as to ensure that no event notifications are missed.
 * @module EVENT
 * @example
 * // Usage:
 * trtc.on(TRTC.EVENT.ERROR, () => {});
 */


export const TRTCEvent = {
  /**
   * Error event, non-API call error, SDK throws when an unrecoverable error occurs during operation.
   *
   * - Error code (error.code): {@link module:ERROR_CODE.OPERATION_FAILED ErrorCode.OPERATION_FAILED}
   * - Possible extended error codes (error.extraCode): 5501, 5502
   * @default 'error'
   * @memberof module:EVENT
   * @see {@link RtcError RtcError}
   * @e
   * @example
   *
   * trtc.on(TRTC.EVENT.ERROR, error => {
   *   console.error('trtc error observed: ' + error);
   *   const errorCode = error.code;
   *   const extraCode = error.extraCode;
   * });
   */
  ERROR: 'error',
  /**
     * @description Automatic playback failed, refer to {@tutorial 21-advanced-auto-play-policy}
     * @default 'autoplay-failed'
     * @memberof module:EVENT
     * @example
     * trtc.on(TRTC.EVENT.AUTOPLAY_FAILED, event => {
     *   // Guide user to click the page, SDK will resume playback automatically when user click the page.
     *   // Since v5.1.3+, you can get userId on this event.
     *   console.log(event.userId);
     *   // Since v5.11.0+, mediaType includes 'audio'|'video'|'screen'.
     *   console.log(event.mediaType);
     *   // Since v5.9.0+, you can call the `resume` method to resume playback of the stream corresponding to event.userId when user clicked the page.
     *   event.resume();
     * });
     */
  AUTOPLAY_FAILED: 'autoplay-failed',
  /**
   * @description Kicked out of the room for some reason, including:<br>
   * - kick: The same user with same userId enters same room. The user who publish stream first will be kicked out of the room by the user who publish stream later.
   *   - Entering a room with the same userId is not allowed behavior, which may lead to abnormal audio/video calls between the two parties, and should be avoided on the business side.
   *   - Users with the same userId who enter the same room with the same audience role may not receive this event.
   * - banned: kicked out by the administrator using [Server API - RemoveUser](https://trtc.io/document/34267/34268).
   * - room_disband: kicked out by the administrator using [Server API - DismissRoom](https://trtc.io/document/34267/34269).
   * @default 'kicked-out'
   * @memberof module:EVENT
   * @example
   *
   * trtc.on(TRTC.EVENT.KICKED_OUT, event => {
   *   console.log(event.reason)
   * });
   */
  KICKED_OUT: 'kicked-out',
  /**
   * Remote user enters the room event.
   *
   * - In `rtc` mode, all users will receive the notification of entering and exiting the room of the other user.
   * - In `live` mode, only the anchor has the notification of entering and exiting the room, and the audience does not have the notification of entering and exiting the room. The audience can receive the notification of entering and exiting the room of the anchor.
   * @default 'remote-user-enter'
   * @memberof module:EVENT
   * @example
   *
   * trtc.on(TRTC.EVENT.REMOTE_USER_ENTER, event => {
   *   const userId = event.userId;
   * });
   */
  REMOTE_USER_ENTER: 'remote-user-enter',
  /**
   * Remote user exits the room event.
   *
   * - In `rtc` mode, all users will receive the notification of entering and exiting the room of the other user.
   * - In `live` mode, only the anchor has the notification of entering and exiting the room, and the audience does not have the notification of entering and exiting the room. The audience can receive the notification of entering and exiting the room of the anchor.
   *
   * `reason` - The reason for leaving is as follows. Since v5.15.0+
   *    | Value | Meaning |
   *    | :--- | :---- |
   *    | 0 | indicates that the user voluntarily left the room |
   *    | 1 | indicates that the user left after the timeout (heartbeat timeout caused by user network issue)|
   *    | 2 | indicates that the user was removed from the room (call the server-side API to kick out the user)|
   *    | 3 | indicates that the anchor left the room because they switched to being a audience |
  * @default 'remote-user-exit'
   * @memberof module:EVENT
   * @example
   *
   * trtc.on(TRTC.EVENT.REMOTE_USER_EXIT, event => {
   *   const userId = event.userId;
   *   const reason = event.reason;
   * });
   */
  REMOTE_USER_EXIT: 'remote-user-exit',
  /**
   * Remote user publishes audio. You will receive this notification when the remote user turns on the microphone. Refer to: [Turn on/off camera and microphone](./tutorial-15-basic-dynamic-add-video.html)
   *
   * - By default, the SDK automatically plays remote audio, and you do not need to call the API to play remote audio. You can listen for this event and {@link module:EVENT.REMOTE_AUDIO_UNAVAILABLE REMOTE_AUDIO_UNAVAILABLE} to update the UI icon for "whether the remote microphone is turned on".
   * - Note: If the user has not interacted with the page before entering the room, automatic audio playback may fail due to the [browser's automatic playback policy restrictions](./tutorial-21-advanced-auto-play-policy.html). You need to refer to the [suggestions for handling automatic playback restrictions](./tutorial-21-advanced-auto-play-policy.html) for processing.
   * - If you do not want the SDK to automatically play audio, you can set `autoReceiveAudio` to `false` to turn off automatic audio playback when calling {@link TRTC#enterRoom trtc.enterRoom()}.
   * - Listen for the {@link module:EVENT.REMOTE_AUDIO_AVAILABLE TRTC.EVENT.REMOTE_AUDIO_AVAILABLE} event, record the userId with remote audio, and call the {@link TRTC#muteRemoteAudio trtc.muteRemoteAudio(userId, false)} method when you need to play audio.
   * @default 'remote-audio-available'
   * @memberof module:EVENT
   * @example
   * // Listen before entering the room
   * trtc.on(TRTC.EVENT.REMOTE_AUDIO_AVAILABLE, event => {
   *   const userId = event.userId;
   * });
   */
  REMOTE_AUDIO_AVAILABLE: 'remote-audio-available',
  /**
   * Remote user stops publishing audio. You will receive this notification when the remote user turns off the microphone.
   *
   * @default 'remote-audio-unavailable'
   * @memberof module:EVENT
   * @example
   * // Listen before entering the room
   * trtc.on(TRTC.EVENT.REMOTE_AUDIO_UNAVAILABLE, event => {
   *   const userId = event.userId;
   *
   * });
   */
  REMOTE_AUDIO_UNAVAILABLE: 'remote-audio-unavailable',
  /**
   * Remote user publishes video. You will receive this notification when the remote user turns on the camera. Refer to: [Turn on/off camera and microphone](./tutorial-15-basic-dynamic-add-video.html)
   *
   * - You can listen for this event and {@link module:EVENT.REMOTE_VIDEO_UNAVAILABLE REMOTE_VIDEO_UNAVAILABLE} to update the UI icon for "whether the remote camera is turned on".
   * @see {@link module:TYPE.STREAM_TYPE_MAIN STREAM_TYPE_MAIN}
   * @see {@link module:TYPE.STREAM_TYPE_SUB STREAM_TYPE_SUB}
   * @default 'remote-video-available'
   * @memberof module:EVENT
   * @example
   * // Listen before entering the room
   * trtc.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, event => {
   *   const userId = event.userId;
   *   const streamType = event.streamType;
   *   trtc.startRemoteVideo({userId, streamType, view});
   * });
   */
  REMOTE_VIDEO_AVAILABLE: 'remote-video-available',
  /**
   * Remote user stops publishing video. You will receive this notification when the remote user turns off the camera.
   * @default 'remote-video-unavailable'
   * @memberof module:EVENT
   * @example
   * // Listen before entering the room
   * trtc.on(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, event => {
   *   const userId = event.userId;
   *   const streamType = event.streamType;
   *   // At this point, the SDK will automatically stop playing, and there is no need to call stopRemoteVideo.
   * });
   */
  REMOTE_VIDEO_UNAVAILABLE: 'remote-video-unavailable',
  /**
   * @description Volume event<br>
   * After calling the {@link TRTC#enableAudioVolumeEvaluation enableAudioVolumeEvaluation} interface to enable the volume callback, the SDK will throw this event regularly to notify the volume of each userId.<br>
   * **Note**
   * - The callback contains the volume of the local microphone and the volume of the remote user. The callback will be triggered regardless of whether anyone is speaking.
   * - The event.result will be sorted from large to small according to the volume size.
   * - When userId is an empty string, it represents the volume of the local microphone.
   * - `volume` is a positive integer with a value of 0-100.
   * - `floatVolume` is a float number with a value of 0-1.0. Since v5.11.1+. The floatVolume will be 0 when the microphone exception occurs. If user is not speaking, the floatVolume will be a non-zero float number.
   * @default 'audio-volume'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.AUDIO_VOLUME, event => {
   *    event.result.forEach(({ userId, volume, floatVolume }) => {
   *        const isMe = userId === ''; // When userId is an empty string, it represents the volume of the local microphone.
   *        if (isMe) {
   *            console.log(`my volume: ${volume}`);
   *        } else {
   *            console.log(`user: ${userId} volume: ${volume}`);
   *        }
   *    })
   * });
   *
   * // Enable volume callback and trigger the event every 1000ms
   * trtc.enableAudioVolumeEvaluation(1000);
   */
  AUDIO_VOLUME: 'audio-volume',
  /**
   * @since v5.8.3
   * @description PCM event of microphone, the pcm data can be used to do ASR. since v5.11.0, support callback of raw pcm data from remote users. <br>
   * **Note**
   * - By default, only the microphone data is called back. If you need to subscribe to the remote user's audio, you need to call trtc.callExperimentalAPI('enableAudioFrameEvent', options) to enable the callback. For details, please visit {@link TRTC#callExperimentalAPI }
   * - After starting the microphone or subscribing to the remote user's audio, this event is triggered every 40ms to obtain pcm data (float32 type).
   * - PCM data is mono and sampled at 48000Hz. Since v5.11.0, Other sampling rates and channel count can be specified through {@link TRTC#callExperimentalAPI }.
   * @default 'audio-frame'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.AUDIO_FRAME, event => {
   *   console.log(event.data) // pcm data. If it is mono, the data type is float32Array, if it is stereo, the data type is float32Array[].
   *   console.log(event.userId) // since v5.11.0. If userId is empty string, it means local microphone.
   *   console.log(event.channelCount) // since v5.11.0. 1 means mono, 2 means stereo.
   *   console.log(event.sampleRate) // since v5.11.0. The sampling rate of the pcm data.
   * });
   *
   */
  AUDIO_FRAME: 'audio-frame',
  /**
   * @description Network quality statistics data event, which starts to be counted after entering the room and triggers every two seconds. This data reflects the network quality of your local uplink and downlink.
   * - The uplink network quality (uplinkNetworkQuality) refers to the network situation of uploading local streams (uplink connection network quality from SDK to Tencent Cloud)
   * - The downlink network quality (downlinkNetworkQuality) refers to the average network situation of downloading all streams (average network quality of all downlink connections from Tencent Cloud to SDK)
   *
   *    The enumeration values and meanings are shown in the following table:
   *    | Value | Meaning | Condition (loss: packet loss rate %, rtt: round-trip time ms) |
   *    | :--- | :---- | :---- |
   *    | 0 | Network state is unknown, indicating that the current trtc instance has not established an uplink/downlink connection | - |
   *    | 1 | Network state is excellent | loss ≤ 10 and rtt ≤ 100 |
   *    | 2 | Network state is good | loss > 10 or rtt > 100 |
   *    | 3 | Network state is average | loss > 20 or rtt > 200 |
   *    | 4 | Network state is poor | loss > 30 or rtt > 350 |
   *    | 5 | Network state is very poor | loss > 50 or rtt > 500 |
   *    | 6 | Network connection is disconnected<br/>Note: If the downlink network quality is this value, it means that all downlink connections have been disconnected | - |
   * - uplinkRTT, uplinkLoss are the uplink RTT (ms) and uplink packet loss rate.
   * - downlinkRTT, downlinkLoss are the average RTT (ms) and average packet loss rate of all downlink connections.
   *
   * **Note**
   * - If you want to know the uplink and downlink network conditions of the other party, you need to broadcast the other party's network quality through IM.
   *
   * @default 'network-quality'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.NETWORK_QUALITY, event => {
   *    console.log(`network-quality, uplinkNetworkQuality:${event.uplinkNetworkQuality}, downlinkNetworkQuality: ${event.downlinkNetworkQuality}`)
   *    console.log(`uplink rtt:${event.uplinkRTT} loss:${event.uplinkLoss}`)
   *    console.log(`downlink rtt:${event.downlinkRTT} loss:${event.downlinkLoss}`)
   * })
   */
  NETWORK_QUALITY: 'network-quality',
  /**
   * @description SDK and Tencent Cloud connection state change event, you can use this event to listen to the overall connection state of the SDK and Tencent Cloud.<br>
   * - 'DISCONNECTED': Connection disconnected
   * - 'CONNECTING': Connecting
   * - 'CONNECTED': Connected
   *
   * Meanings of different state changes:
   *
   * - DISCONNECTED -> CONNECTING: Trying to establish a connection, triggered when calling the enter room interface or when the SDK automatically reconnects.
   * - CONNECTING -> DISCONNECTED: Connection establishment failed, triggered when calling the exit room interface to interrupt the connection or when the connection fails after SDK retries.
   * - CONNECTING -> CONNECTED: Connection established successfully, triggered when the connection is successful.
   * - CONNECTED -> DISCONNECTED: Connection interrupted, triggered when calling the exit room interface or when the connection is disconnected due to network anomalies.
   *
   * Suggestion: You can listen to this event and display different UIs in different states to remind users of the current connection state.
   *
   * @default 'connection-state-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.CONNECTION_STATE_CHANGED, event => {
   *   const prevState = event.prevState;
   *   const curState = event.state;
   *   if (curState === 'CONNECTING') {
   *     const isReconnecting = event.isReconnecting; // Since v5.15.0
   *   }
   * });
   */
  CONNECTION_STATE_CHANGED: 'connection-state-changed',
  /**
   * @description Audio playback state change event
   *
   * event.userId When userId is an empty string, it represents the local user, and when it is a non-empty string, it represents a remote user.
   *
   * event.state The value is as follows:
   * - 'PLAYING': start playing
   *   - event.reason is 'playing' or 'unmute'.
   * - 'PAUSED': pause playback
   *   - When event.reason is 'pause', it is triggered by the pause event of the \<audio\> element. The following situations will trigger:
   *      - Call the HTMLMediaElement.pause interface.
   *   - When event.reason is 'mute'. See event {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/mute_event | MediaStreamTrack.mute_event}
   *      - When userId is oneself, this event is triggered, indicating that audio collection is paused, usually caused by device abnormalities, such as being preempted by other applications on the device, at this time, the user needs to be guided to recollect.
   *      - When userId is others, this event is triggered, indicating that the received audio data is not enough to play. Usually caused by network jitter, no processing is required on the access side. When the received data is sufficient to play, it will automatically resume.
   * - 'STOPPED': stop playing
   *   - event.reason is 'ended'.
   *
   * event.reason The reason for the state change, the value is as follows:
   * - 'playing': start playing, see event {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playing_event | HTMLMediaElement.playing_event}
   * - 'mute': The audio track cannot provide data temporarily, see event {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/mute_event | MediaStreamTrack.mute_event}
   * - 'unmute': The audio track resumes providing data, see event {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/unmute_event | MediaStreamTrack.unmute_event}
   * - 'ended': The audio track has been closed
   * - 'pause': Playback paused
   * @default 'audio-play-state-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.AUDIO_PLAY_STATE_CHANGED, event => {
   *   console.log(`${event.userId} player is ${event.state} because of ${event.reason}`);
   * });
   */
  AUDIO_PLAY_STATE_CHANGED: 'audio-play-state-changed',
  /**
   * @description Video playback state change event
   *
   * event.userId When userId is an empty string, it represents the local user, and when it is a non-empty string, it represents a remote user.
   *
   * event.streamType Stream type, value: {@link module:TYPE.STREAM_TYPE_MAIN TRTC.TYPE.STREAM_TYPE_MAIN} {@link module:TYPE.STREAM_TYPE_SUB TRTC.TYPE.STREAM_TYPE_SUB}
   *
   * event.state The value is as follows:
   * - 'PLAYING': start playing
   *   - event.reason is 'playing' or 'unmute'.
   * - 'PAUSED': pause playback
   *   - When event.reason is 'pause', it is triggered by the pause event of the \<video\> element. The following situations will trigger:
   *      - Call the HTMLMediaElement.pause interface.
   *      - After successful playback, the view container for playing the video is removed from the DOM.
   *   - When event.reason is 'mute'. See event {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/mute_event | MediaStreamTrack.mute_event}
   *      - When userId is oneself, this event is triggered, indicating that video collection is paused, usually caused by device abnormalities, such as being preempted by other applications on the device, at this time, the user needs to be guided to recollect.
   *      - When userId is others, this event is triggered, indicating that the received video data is not enough to play. Usually caused by network jitter, no processing is required on the access side. When the received data is sufficient to play, it will automatically resume.
   * - 'STOPPED': stop playing
   *   - event.reason is 'ended'.
   *
   * event.reason The reason for the state change, the value is as follows:
   * - 'playing': start playing, see event {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playing_event | HTMLMediaElement.playing_event}
   * - 'mute': The video track cannot provide data temporarily, see event {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/mute_event | MediaStreamTrack.mute_event}
   * - 'unmute': The video track resumes providing data, see event {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack/unmute_event | MediaStreamTrack.unmute_event}
   * - 'ended': The video track has been closed
   * - 'pause': Playback paused
   * @default 'video-play-state-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.VIDEO_PLAY_STATE_CHANGED, event => {
   *   console.log(`${event.userId} ${event.streamType} video player is ${event.state} because of ${event.reason}`);
   * });
   */
  VIDEO_PLAY_STATE_CHANGED: 'video-play-state-changed',
  /**
   * @description Notification event for local screen sharing stop, only valid for local screen sharing streams.
   * @default 'screen-share-stopped'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.SCREEN_SHARE_STOPPED, () => {
   *   console.log('screen sharing was stopped');
   * });
   */
  SCREEN_SHARE_STOPPED: 'screen-share-stopped',
  /**
   * @description Notification event for device changes such as camera and microphone.
   * - event.device is a [MediaDeviceInfo](https://developer.mozilla.org/en-US/docs/Web/API/MediaDeviceInfo) object with properties:
   *    - deviceId: device ID
   *    - label: device description information
   *    - groupId: device group ID
   * - event.type value: `'camera'|'microphone'|'speaker'`
   * - event.action value:
   *    - 'add' device has been added.
   *    - 'remove' device has been removed.
   *    - 'active' device has been activated, for example: after startLocalVideo is successful, this event will be triggered.
   * @default 'device-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.DEVICE_CHANGED, (event) => {
   *   console.log(`${event.type}(${event.device.label}) ${event.action}`);
   * });
   */
  DEVICE_CHANGED: 'device-changed',
  /**
   * @description Publish state change event.
   * - event.mediaType media type, value: `'audio'|'video'|'screen'`.
   * - event.state current publish state, value:
   *    - `'starting'` trying to publish stream
   *    - `'started'` publish stream succeeded
   *    - `'stopped'` publish stream stopped, see event.reason field for the reason
   * - event.prevState the publish state at the last event trigger, with the same type as event.state.
   * - event.reason the reason for the publish state to become `'stopped'`, value:
   *    - `'timeout'` publish stream timeout, usually caused by network jitter or firewall interception. The SDK will keep retrying, and the business side can guide the user to check the network or change the network at this time.
   *    - `'error'` publish stream error, at this time, you can get the specific error information from event.error, usually caused by the browser not supporting H264 encoding.
   *    - `'api-call'` publish stream stopped due to business side API call, for example, stopLocalVideo was called to stop the publish stream before startLocalVideo was successful, which is a normal behavior and the business side does not need to pay attention to it.
   * - event.error error information when event.reason is `'error'`.
   * @default 'publish-state-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.PUBLISH_STATE_CHANGED, (event) => {
   *   console.log(`${event.mediaType} ${event.state} ${event.reason}`);
   * });
   */
  PUBLISH_STATE_CHANGED: 'publish-state-changed',
  /**
   * @since v5.3.0
   * @description a new MediaStreamTrack object received.
   * @default 'track'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.TRACK, event => {
   *   // userId === '' means event.track is a local track, otherwise it's a remote track
   *   const isLocal = event.userId === '';
   *   // Usually the sub stream is a screen-sharing video stream.
   *   const isSubStream = event.streamType === TRTC.TYPE.STREAM_TYPE_SUB;
   *   const mediaStreamTrack = event.track;
   *   const kind = event.track.kind; // audio or video
   * })
   */
  TRACK: 'track',
  /**
   * @description TRTC statistics.<br>
   *
   * - SDK will fires this event once every 2s.
   * - You can get the network quality, statistics of audio and video from this event. For detailed parameter description, please refer to {@link TRTCStatistics}.
   * @default 'statistics'
   * @since v5.2.0
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.STATISTICS, statistics => {
   *    console.warn(statistics.rtt, statistics.upLoss, statistics.downLoss);
   * })
   */
  STATISTICS: 'statistics',
  /**
   * @since v5.3.0
   * @description SEI message received<br>
   * @default 'sei-message'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.SEI_MESSAGE, event => {
   *    console.log(`received sei message from ${event.userId}, data: ${event.data}, streamType: ${event.streamType}`)
   * })
   */
  SEI_MESSAGE: 'sei-message',
  /**
   * @since v5.6.0
   * @description received a new custom message.
   * @default 'custom-message'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.CUSTOM_MESSAGE, event => {
   *    // event.userId: remote userId.
   *    // event.cmdId: message cmdId.
   *    // event.seq: message sequence number.
   *    // event.data: custom message data, type is ArrayBuffer.
   * })
   */
  CUSTOM_MESSAGE: 'custom-message',
  /**
   * @private
   * @since v5.9.0
   * @description video decode downgrade event.
   * @default 'video-decode-downgrade-state-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.VIDEO_DECODE_DOWNGRADE_STATE_CHANGED, event => {
   *    // event.reason: reason.
   *    // event.state: STARTING | STARTED | FAILED.
   *    // event.preState: STARTING | STARTED.
   *    // event.streamType: TRTCStreamType.Main | TRTCStreamType.Sub.
   *    // event.userId: userId.
   *    // event.type: 'webCodecs' | 'wasm'.
   *    // event.renderer: 'webgl' | '2d' | 'videoFrame'.
   * })
   */
  VIDEO_DECODE_DOWNGRADE_STATE_CHANGED: 'video-decode-downgrade-state-changed',
  /**
   * @private
   * @since v5.9.0
   * @description voov mouse info
   * @default 'layerData'
   * @memberof module:EVENT
   */
  LAYER_DATA: 'layerData',
  /**
   * @since v5.9.0
   * @description started rendering the first video frame of the local or a remote user.
   * @default 'first-video-frame'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.FIRST_VIDEO_FRAME, event => {
   *    // event.height: video height.
   *    // event.width: video width.
   *    // event.streamType: video stream type.
   *    // event.userId: The user ID of the local or a remote user. If it is empty, it indicates that the first local video frame is available; if it is not empty, it indicates that the first video frame of a remote user is available.
   * })
   */
  FIRST_VIDEO_FRAME: 'first-video-frame',
  /**
   * @since v5.13.0
   * @description Permission state change event
   * @default 'permission-state-change'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.PERMISSION_STATE_CHANGE, event => {
   *    // event.camera: camera permission state.
   *    // event.microphone: microphone permission state.
   * })
   */
  PERMISSION_STATE_CHANGE: 'permission-state-change',
  /**
   * @since v5.13.0
   * @description Video size changed event
   * @default 'video-size-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.VIDEO_SIZE_CHANGED, event => {
   *    // event.newHeight: video height.
   *    // event.newWidth: video width.
   *    // event.streamType: video stream type.
   *    // event.userId: The user ID of the local or a remote user. If it is empty, it indicates that video is the local video and the local user has not entered the room; if it is not empty, it indicates that video is remote video or local user enter room already.
   * })
   */
  VIDEO_SIZE_CHANGED: 'video-size-changed',
  /**
   * @since v5.15.0
   * @description Realtime transcriber message event. Triggered when receiving transcription or translation results from the ASR robot.
   * @default 'realtime-transcriber-message'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.REALTIME_TRANSCRIBER_MESSAGE, event => {
   *    // event.segmentId: Unique identifier for this speech segment
   *    // event.speakerUserId: User ID of the speaker
   *    // event.sourceText: Transcribed text in the source language
   *    // event.translationTexts: Array of translated texts (if translation is enabled)
   *    // event.timestamp: Timestamp in milliseconds (UTC)
   *    // event.isCompleted: Whether this segment is complete (true) or intermediate (false)
   *    // event.robotId: The ID of the transcriber robot
   * })
   */
  REALTIME_TRANSCRIBER_MESSAGE: 'realtime-transcriber-message',
  /**
   * @since v5.15.0
   * @description Realtime transcriber state changed
   * @default 'realtime-transcriber-state-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.REALTIME_TRANSCRIBER_STATE_CHANGED, event => {
   *    // event.state: transcriber state.
   * })
   */
  REALTIME_TRANSCRIBER_STATE_CHANGED: 'realtime-transcriber-state-changed',
  /**
   * @since v5.15.0
   * @description Picture in picture state change event
   * @default 'picture-in-picture-state-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.PICTURE_IN_PICTURE_STATE_CHANGED, event => {
   *    // event.userId: userId.
   *    // event.streamType: video stream type.
   *    // event.isPictureInPicture: is in picture in picture.
   *    // event.pictureInPictureWindow: Exist when isPictureInPicture is true. See: https://developer.mozilla.org/en-US/docs/Web/API/PictureInPictureWindow
   * })
   */
  PICTURE_IN_PICTURE_STATE_CHANGED: 'picture-in-picture-state-changed',
  /**
   * @since v5.15.0
   * @description Full screen state change event
   * @default 'full-screen-state-changed'
   * @memberof module:EVENT
   * @example
   * trtc.on(TRTC.EVENT.FULL_SCREEN_STATE_CHANGED, event => {
   *    // event.userId: userId.
   *    // event.streamType: video stream type.
   *    // event.isFullScreen: is in full screen.
   * })
   */
  FULL_SCREEN_STATE_CHANGED: 'full-screen-state-changed'
} as const;

export const TRTCEventEmitLogToIgnore: Set<Partial<keyof TRTCEventTypes>> = new Set([
  TRTCEvent.AUDIO_VOLUME,
  TRTCEvent.AUDIO_FRAME,
  TRTCEvent.NETWORK_QUALITY,
  TRTCEvent.STATISTICS,
  TRTCEvent.SEI_MESSAGE,
  TRTCEvent.CUSTOM_MESSAGE,
  TRTCEvent.LAYER_DATA
]);

// type eventType = typeof TRTCEvent;
// export type TRTCEventType = eventType[keyof eventType];
export interface TRTCEventTypes {
  [TRTCEvent.ERROR]: [RtcError],
  [TRTCEvent.AUTOPLAY_FAILED]: [{ userId: string; mediaType: 'audio' | 'video' | 'screen', resume: () => Promise<any>; }],
  [TRTCEvent.KICKED_OUT]: [{ reason: Exclude<BannedReason, 'user_time_out'>; }],
  [TRTCEvent.REMOTE_USER_ENTER]: [{ userId: string; }],
  [TRTCEvent.REMOTE_USER_EXIT]: [{ userId: string, reason?: keyof typeof PEER_LEAVE_REASON; }],
  [TRTCEvent.REMOTE_AUDIO_AVAILABLE]: [{ userId: string; }],
  [TRTCEvent.REMOTE_AUDIO_UNAVAILABLE]: [{ userId: string; }],
  [TRTCEvent.REMOTE_VIDEO_AVAILABLE]: [{ userId: string, streamType: TRTCStreamType; }],
  [TRTCEvent.REMOTE_VIDEO_UNAVAILABLE]: [{ userId: string, streamType: TRTCStreamType; }],
  [TRTCEvent.AUDIO_FRAME]: [{ data: Float32Array | Float32Array[]; userId: string; channelCount: number; sampleRate: number; }],
  [TRTCEvent.AUDIO_VOLUME]: [{ result: { userId: string, volume: number; floatVolume: number }[]; }],
  [TRTCEvent.NETWORK_QUALITY]: [NetworkQuality],
  [TRTCEvent.CONNECTION_STATE_CHANGED]: [{ prevState: ConnectionState, state: ConnectionState; isReconnecting?: boolean; }],
  [TRTCEvent.AUDIO_PLAY_STATE_CHANGED]: [{ userId: string, state: PlayerState, reason: string; }],
  [TRTCEvent.VIDEO_PLAY_STATE_CHANGED]: [{ userId: string, streamType: TRTCStreamType, state: PlayerState, reason: string; }],
  [TRTCEvent.SCREEN_SHARE_STOPPED]: [],
  [TRTCEvent.DEVICE_CHANGED]: [{ type: TRTCDeviceType, action: TRTCDeviceAction, device: DeviceInfo; }],
  [TRTCEvent.PUBLISH_STATE_CHANGED]: [{
    mediaType: 'audio' | 'video' | 'screen',
    state: 'started' | 'stopped' | 'starting',
    prevState: 'started' | 'stopped' | 'starting',
    // 状态变为 stopped 的原因
    // timeout 连接超时 ----> 业务侧无需处理，可以提示给用户检查网络
    // error 服务器错误，拒绝推流，例如不支持 h264 编码的设备上，并且没配置 vp8 编码白名单时，尝试推流 -----> 业务侧需要处理，例如申请 vp8 白名单
    // api_call 业务侧 api 调用导致，例如：推流重试过程中，业务侧调用 stopLocalVideo, exitRoom 等 api 停止推流，会停止重试。 ----> 业务侧无需处理
    reason?: 'timeout' | 'error' | 'api-call',
    error?: RtcError;
  }];
  [TRTCEvent.SEI_MESSAGE]: [{ data: ArrayBuffer; userId: string; streamType: TRTCStreamType; seiPayloadType: number; }];
  [TRTCEvent.STATISTICS]: [statistics: TRTCStatistics];
  [TRTCEvent.TRACK]: [{ userId: string; streamType?: TRTCStreamType; track: MediaStreamTrack; sourceTrack: MediaStreamTrack; }];
  [TRTCEvent.CUSTOM_MESSAGE]: [CustomMessage];
  [TRTCEvent.VIDEO_DECODE_DOWNGRADE_STATE_CHANGED]: [{ reason?: string; state: VideoDecoderDowngradeState; prevState: VideoDecoderDowngradeState, type: string, renderer: string, streamType: TRTCStreamType, userId: string; }];
  [TRTCEvent.LAYER_DATA]: [{ userId: string, tinyId: string, layerData: Uint8Array }];
  [TRTCEvent.FIRST_VIDEO_FRAME]: [{ width: number; height: number; streamType: TRTCStreamType; userId: string; }];
  [TRTCEvent.PERMISSION_STATE_CHANGE]: [{ camera: PermissionState | null; microphone: PermissionState | null; }];
  [TRTCEvent.VIDEO_SIZE_CHANGED]: [{ newWidth: number; newHeight: number; streamType: TRTCStreamType; userId: string; }];
  [TRTCEvent.REALTIME_TRANSCRIBER_MESSAGE]: [RealtimeTranscriberMessage];
  [TRTCEvent.REALTIME_TRANSCRIBER_STATE_CHANGED]: [{ state: 'started' | 'stopped'; roomId: string | number; transcriberRobotId: string; }];
  [TRTCEvent.PICTURE_IN_PICTURE_STATE_CHANGED]: [PictureInPictureStateChangedEvent];
  [TRTCEvent.FULL_SCREEN_STATE_CHANGED]: [FullScreenStateChangedEvent]
}
